import { useEffect } from 'react'
import { isEmpty } from 'lodash'
import { useHistory, useParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import routes from 'app/routes'

import { logOutThunk } from 'features/auth/login/slice'
import {
  currentRootIdSlice,
  fetchRootDomainsThunk,
} from 'features/rootDomains/slice'
import {
  currentRootDomainIdSelector,
  rootDomainsDataSelector,
} from 'features/rootDomains/selectors'

interface UrlRootDomainIdParams {
  rootDomainId: string
}

function useUrlRootDomainIdParams() {
  return useParams<UrlRootDomainIdParams>()
}

function useRootDomainId() {
  const dispatch = useAppDispatch()
  const history = useHistory()

  const { rootDomainId } = useUrlRootDomainIdParams()
  const currentRootDomainId = useAppSelector(currentRootDomainIdSelector)
  const rootDomains = useAppSelector(rootDomainsDataSelector.data)
  const isFetchingRootDomains = useAppSelector(
    rootDomainsDataSelector.isFetching
  )
  const isFinishedRootDomains = useAppSelector(
    rootDomainsDataSelector.isFinished
  )
  const hasRootDomainsError = useAppSelector(rootDomainsDataSelector.error)

  // Check if urlRootId is among user's rootDomains
  const isValidUrlRootId = !!rootDomains?.find(
    (domain) => domain.id === rootDomainId
  )

  useEffect(() => {
    if (rootDomains && !isValidUrlRootId) {
      console.info('Not valid url root domain id or it was empty')
      if (currentRootDomainId) {
        history.push(routes.homesJobs(currentRootDomainId))
      }
    }
  }, [currentRootDomainId, history, isValidUrlRootId, rootDomains])

  useEffect(() => {
    // Change store's currentRootDomainId to URL's rootDomainId if it's valid user's root domain id
    if (
      rootDomains &&
      isValidUrlRootId &&
      rootDomainId !== currentRootDomainId
    ) {
      dispatch(currentRootIdSlice.actions.setId(rootDomainId))
    }
  }, [
    currentRootDomainId,
    dispatch,
    isValidUrlRootId,
    rootDomainId,
    rootDomains,
  ])

  useEffect(() => {
    if (
      !currentRootDomainId &&
      !isFetchingRootDomains &&
      !isFinishedRootDomains &&
      !hasRootDomainsError
    ) {
      dispatch(fetchRootDomainsThunk())
    }
  }, [
    dispatch,
    isFetchingRootDomains,
    isFinishedRootDomains,
    hasRootDomainsError,
    currentRootDomainId,
  ])

  useEffect(() => {
    if (
      hasRootDomainsError ||
      (isFinishedRootDomains && isEmpty(rootDomains))
    ) {
      // TODO: instead of console msg show error screen and then logout
      console.log(' User is not added to any domain or something went wrong')
      dispatch(logOutThunk())
    }
  }, [dispatch, isFinishedRootDomains, rootDomains, hasRootDomainsError])

  return [rootDomainId]
}

export default useRootDomainId
